"use client";

import type { Namespace } from "@calcom/i18n/useLocale";
import i18next from "i18next";
import type { ReactNode } from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";

let globalI18nInitialized = false;

function ensureI18nInitialized(locale: string, ns: Namespace, translations: Record<string, string>) {
  if (!globalI18nInitialized) {
    i18next.use(initReactI18next).init({
      lng: locale,
      resources: { [locale]: { [ns]: translations } },
      defaultNS: ns,
      fallbackNS: "common",
      interpolation: { escapeValue: false },
      react: { nsMode: "fallback" },
    });
    globalI18nInitialized = true;
    return;
  }
  // Update existing instance with new/changed translations (deep-merge)
  i18next.addResourceBundle(locale, ns, translations, true, true);
  if (i18next.language !== locale) {
    i18next.changeLanguage(locale);
  }
}

/**
 * Hydrates react-i18next globally with server-loaded translations.
 * Used by the root layout (common translations) and booking pages (locale override).
 */
export function I18nProvider({
  children,
  translations,
  locale,
  ns,
}: {
  children: ReactNode;
  translations: Record<string, string>;
  locale: string;
  ns: Namespace;
}) {
  // Always sync the global instance's language with our locale prop.
  // This handles the case where a nested provider (e.g. booking page) changed
  // the global language and the user navigated back.
  ensureI18nInitialized(locale, ns, translations);

  return <I18nextProvider i18n={i18next}>{children}</I18nextProvider>;
}

/**
 * Registers server-loaded translations for a route-specific namespace with
 * react-i18next. Consumed from a nested route layout so pages can call
 * useLocale("some_ns") and fall back to "common" automatically via
 * fallbackNS configured in ensureI18nInitialized.
 */
export function I18nExtend({
  children,
  translations,
  ns,
}: {
  children: ReactNode;
  translations: Record<string, string>;
  ns: Namespace;
}) {
  const locale = i18next.language ?? "en";
  i18next.addResourceBundle(locale, ns, translations, true, true);

  return <>{children}</>;
}
